'use client'
import React, { useEffect, useRef, useState, forwardRef } from 'react'
import { useLink } from '@/helpers/linkAdapter'

import styles from './Button.module.scss'
import { ButtonProps } from './Button.types'

/**
 * Button component for user interaction
 * Supports rendering as different elements via the `as` prop
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      colorScheme = 'black',
      as = 'button',
      variant = 'primary',
      size = 'medium',
      theme = 'light',
      disabled = false,
      children,
      className = '',
      onClick,
      href,
      target,
      rel,
      Icon = () => <></>,
      iconPosition = 'right',
      autoWidth = false,
      ...props
    },
    ref
  ) => {
    const Component = useLink(as, href)
    const hasChildren =
      children !== undefined && children !== null && !!children
    const childrenRef = useRef<HTMLSpanElement>(null)
    const [isOverflowing, setIsOverflowing] = useState(false)
    const [overflowValue, setOverflowValue] = useState(0)

    const buttonClasses = [
      styles.button,
      styles[`button--${variant}`],
      styles[`button--${size}`],
      styles[theme],
      styles[`${disabled ? 'disabled' : 'enabled'}`],
      isOverflowing ? styles['button--is-overflowing'] : '',
      !hasChildren ? styles['button--no-children'] : '',
      autoWidth ? styles['autoWidth'] : '',
      className,
    ]
      .filter(Boolean)
      .join(' ')

    const isLink = href !== undefined && href !== null
    const linkProps = isLink && href ? { href } : {}
    const hasValidIcon = Icon && typeof Icon === 'function'

    // For accessibility, add noreferrer and noopener when target is _blank
    const finalRel =
      target === '_blank' ? `${rel || ''} noreferrer noopener`.trim() : rel

    // Prepare finalized props based on if it's a link or not
    const finalProps = {
      className: buttonClasses,
      onClick,
      disabled: isLink ? undefined : disabled, // HTML links don't support disabled
      'aria-disabled': isLink && disabled ? true : undefined,
      ...(isLink && { target, rel: finalRel }),
      ...linkProps,
      ...props,
    }

    // If it's a disabled link, we prevent the default behavior
    const handleClick =
      isLink && disabled
        ? (e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault()
            e.stopPropagation()
          }
        : onClick

    useEffect(() => {
      if (childrenRef.current && hasChildren) {
        const checkOverflow = () => {
          const element = childrenRef.current
          if (element) {
            // Compare scrollWidth (full content width) with offsetWidth (visible width)
            const isTextOverflowing = element.scrollWidth > element.offsetWidth
            setIsOverflowing(isTextOverflowing)
            setOverflowValue(element.scrollWidth + 28)
          }
        }

        checkOverflow()

        window.addEventListener('resize', checkOverflow)
        return () => {
          window.removeEventListener('resize', checkOverflow)
        }
      }
    }, [hasChildren, children])

    return (
      <Component
        {...finalProps}
        onClick={handleClick}
        data-overflow-value={overflowValue}
        ref={ref}
        data-color-scheme={colorScheme}
      >
        {iconPosition === 'left' && hasValidIcon && (
          <Icon className={styles.button__icon} />
        )}
        {hasChildren && (
          <span
            ref={childrenRef}
            data-overflow-value={overflowValue}
            className={`${styles.button__children} ${isOverflowing ? styles['is-overflowing'] : ''}`}
          >
            {children}
          </span>
        )}
        {iconPosition === 'right' && hasValidIcon && (
          <Icon className={styles.button__icon} />
        )}
      </Component>
    )
  }
)

Button.displayName = 'Button'

export default Button
